import type { ShipmentDetail } from "api";
import { carrierLabel, equipmentLabel, isPending, shipmentCode } from "components/Shipments/shipments.model";

export type DetailTab = {
  key: string;
  label: string;
  /** URL segment after the shipment id. The default tab never appears in the path. */
  slug: string;
  /** Kept in the model but out of the tab strip and out of the router. */
  hidden?: boolean;
};

/**
 * The tabs of one shipment, as the design's `.shipp__tabs` strip lays them
 * out. Tracking, Financial and Notes & Activity are drawn as empty panels in
 * the prototype, so like the hidden entries in `nav.model` they keep a key and
 * a slug but stay unreachable until they have a design to build from — the
 * back office already serves Financial and Notes, and Tracking reads through
 * `/api/tools/*`.
 */
export const DETAIL_TABS: DetailTab[] = [
  { key: "cargo", label: "Cargo", slug: "cargo" },
  { key: "tracking", label: "Tracking", slug: "tracking", hidden: true },
  { key: "docs", label: "Documents", slug: "documents" },
  { key: "costs", label: "Financial", slug: "financial", hidden: true },
  { key: "notes", label: "Notes & Activity", slug: "notes", hidden: true },
];

/** What opening a shipment lands on. */
export const DEFAULT_DETAIL_TAB = "cargo";

/** The tabs that are built — the only ones the strip or a URL gets. */
export const VISIBLE_DETAIL_TABS: DetailTab[] = DETAIL_TABS.filter((tab) => tab.hidden !== true);

/**
 * The grey line under the number, as the design's `fillDetail` builds it:
 * carrier, booking number, then the equipment.
 */
export const headerMeta = (shipment: ShipmentDetail): string => {
  const booking = shipment.booking_number ?? (shipment.type === "BOOKING" ? shipment.reference : null);

  // `size_type` is already counted ("2 x 40HC"), so the equipment needs no number in front.
  return [carrierLabel(shipment), booking, equipmentLabel(shipment)].filter(Boolean).join("  ·  ");
};

/** The big number in the header. A card with no number yet has only its code. */
export const headerNumber = (shipment: ShipmentDetail): string =>
  isPending(shipment) ? shipmentCode(shipment) : (shipment.master_bl ?? shipment.reference);
